import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { KeyRound, Loader, CheckCircle, XCircle } from 'lucide-react';
import { apiRequest } from '../../apiClient';

export default function RequestResetPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      const response = await apiRequest<{ success: boolean }>('/api/students/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email })
      });

      if (response.success) {
        setSuccess(true);
      } else {
        setError('Failed to send reset link. Please try again.');
      }
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cool-gray-100 to-blue-50 py-8 px-4">
      <div className="w-full max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-midnight-indigo p-3 rounded-2xl credential-capital-shadow-lg">
              <KeyRound className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold text-midnight-indigo mb-2">
            Reset Password
          </h1>
          <p className="text-warm-slate">
            Enter your email to receive a password reset link
          </p>
        </div>

        {/* Form Card */}
        <Card className="credential-capital-card">
          <CardHeader className="text-center pb-3">
            <CardTitle className="text-xl font-display text-charcoal-ink">
              Request Password Reset
            </CardTitle>
            <CardDescription className="text-warm-slate text-sm">
              We'll send you an email with instructions to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            {success ? (
              <div className="text-center py-6">
                <CheckCircle className="h-12 w-12 text-verified-green mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-charcoal-ink mb-2">
                  Check Your Email
                </h3>
                <p className="text-sm text-warm-slate mb-4">
                  If an account exists with {email}, you'll receive a password reset link shortly.
                </p>
                <Button 
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="text-sm"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-charcoal-ink font-medium text-sm">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="credential-capital-input h-10"
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-10 credential-capital-button-primary text-sm font-medium"
                  disabled={isLoading || !email}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Sending Reset Link...
                    </div>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm text-warm-slate hover:text-midnight-indigo transition-colors"
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-warm-slate">
            Powered by Credential Capital • Secure • Verifiable • Trusted
          </p>
        </div>
      </div>
    </div>
  );
}